import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { Client, IAttachmentDto, TimelineItemType } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { AuthService, LoaderService, Role } from '@fdm/services';
import {
  CreateNoteDialog,
  CreateNoteDialogResponse,
  TimelineComponent,
  TimelineRecord,
} from '@fdm/shared';
import {
  catchError,
  combineLatest,
  finalize,
  first,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ApprovalFlowService, ReportFormStore } from '../../../../services';
import { PolicyService } from '../../../../services/roles/policy.service';
import { AttachmentsComponent } from './attachments/attachments.component';
import {
  NoteAttachmentApiService,
  UpdateNotePayload,
} from './note-attachment-api.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-note-attachment-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    AttachmentsComponent,
    MatButtonModule,
    MatIconModule,
    TimelineComponent,
  ],
  templateUrl: './note-attachment-tab.component.html',
  styleUrl: './note-attachment-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NoteAttachmentApiService],
})
export class NoteAttachmentTabComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _apiClient = inject(Client);
  private _api = inject(NoteAttachmentApiService);
  private _destroyRef = inject(DestroyRef);
  private _store = inject(ReportFormStore);
  private _dialog = inject(MatDialog);
  private _loader = inject(LoaderService);
  private _policy = inject(PolicyService);
  private _approvalFlow = inject(ApprovalFlowService);
  private _auth = inject(AuthService);
  private _toastrService = inject(ToastrService);

  public canCreate$ = this._policy.canCreateNote$;

  public timelineList = signal<TimelineRecord[]>([]);
  public attachments = signal<IAttachmentDto[]>([]);

  private _generalTabId: string | null = null;

  private _riskTabData = toSignal(this._store.risk$);
  private _roles = toSignal(this._auth.roles$);

  public isOwner = computed(() => {
    const roles = this._roles();

    return (
      roles?.includes(Role.Creator) || roles?.includes(Role.Admin) || false
    );
  });

  ngOnInit(): void {
    this._initTriggers();
  }

  private _initTriggers(): void {
    this._approvalFlow.flowEventsUpdated
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._getTimeLine();
        this._getAttachments();
      });

    combineLatest([this._route.paramMap, this._store.risk$])
      .pipe(
        tap(([params, _]) => {
          this._generalTabId = params.get('id');
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        this._getTimeLine();
        this._getAttachments();
      });
  }

  private _getTimeLine() {
    if (!this._generalTabId) {
      return;
    }

    this._apiClient
      .timeline(this._generalTabId)
      .pipe(
        first(),
        map((res) =>
          res.map((item) => {
            const author = item.author?.split(',');

            return {
              ...item,
              name: author ? author[0][0] + author[1].trim()[0] : null,
            } as TimelineRecord;
          }),
        ),
      )
      .subscribe((timeline) => {
        this.timelineList.set(timeline);
      });
  }

  private _getAttachments() {
    if (!this._generalTabId) {
      return;
    }

    this._api
      .getAttachments(this._generalTabId)
      .pipe(first())
      .subscribe((attachments) => {
        this.attachments.set(attachments);
      });
  }

  public createNote() {
    this._dialog
      .open(CreateNoteDialog)
      .afterClosed()
      .pipe(
        first(),
        mergeMap((res) => {
          if (res && this._generalTabId) {
            this._loader.startProcessing();

            return this._api.createNote(this._generalTabId, res).pipe(
              tap(() => {
                this._toastrService.success('Note created successfully');
                this._getTimeLine();
                this._getAttachments();
              })
            );
          }

          return of(null);
        }),
        catchError((err) => {
          this._loader.stopProcessing();
          this._toastrService.error('Failed to create Note');
          return of(null);
        }),
        finalize(() => this._loader.stopProcessing()),
      )
      .subscribe();
  }

  public editNote(idx: number) {
    const data = this.timelineList()[idx];

    if (!data) {
      return;
    }

    this._dialog
      .open<CreateNoteDialog, TimelineRecord, CreateNoteDialogResponse>(
        CreateNoteDialog,
        { data },
      )
      .afterClosed()
      .pipe(
        first(),
        mergeMap((res) => {
          if (res && this._generalTabId) {
            this._loader.startProcessing();

            if (!data.noteId) {
              this._toastrService.error('Note ID is missing');
            }

            const payload: UpdateNotePayload = {
              ...res,
              filesAttachedNoteId: data.noteId as number,
              noteId: (data.attachments?.[0]?.id ?? data.noteId) as number,
            };

            return this._api
              .updateNote(this._generalTabId, payload)
              .pipe(
                tap(() => {
                  this._toastrService.success('Note updated successfully');
                  this._getTimeLine();
                  this._getAttachments();
                }),
              );
          }

          return of(null);
        }),
        catchError((err) => {
          this._loader.stopProcessing();
          this._toastrService.error(
            'Failed to update the Note with ID: ' + this._generalTabId,
          );
          return of(null);
        }),
        finalize(() => this._loader.stopProcessing()),
      )
      .subscribe();
  }

  public deleteNote(noteId: number | null) {
    if (!noteId) {
      this._toastrService.error('Note ID is missing');
      return;
    }

    this._apiClient
      .notesDELETE(noteId)
      .pipe(
        first(),
        tap(() => {
          this._toastrService.success('Note deleted successfully');
          this._getTimeLine();
          this._getAttachments();
        }),
        catchError((err) => {
          this._toastrService.error('Note deletion failed');
          return of(null);
        }),
      )
      .subscribe();
  }

  public download(blobName?: string) {
    if (!blobName) {
      return;
    }

    this._api.download(blobName).subscribe();
  }
}
